import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const FROM_MAP = {
  fadeIn:     { opacity: 0, y: 55, scale: 1, x: 0 },
  scaleIn:    { opacity: 0, y: 20, scale: 0.91, x: 0 },
  slideLeft:  { opacity: 0, x: -75, y: 0, scale: 1 },
  slideRight: { opacity: 0, x: 75, y: 0, scale: 1 },
  fadeUp:     { opacity: 0, y: 80, scale: 1, x: 0 },
};

/**
 * ScrollAnimatedSection — GSAP ScrollTrigger powered.
 * Props:
 *   animationType: 'fadeIn' | 'scaleIn' | 'slideLeft' | 'slideRight' | 'fadeUp'
 *   delay: number (seconds)
 *   stagger: false | number — if set, animates direct children with stagger instead of container
 *   className / ...props passed to wrapper div
 */
function ScrollAnimatedSection({
  children,
  animationType = 'fadeIn',
  className = '',
  delay = 0,
  stagger = false,
  ...props
}) {
  const elRef = useRef(null);

  useEffect(() => {
    const el = elRef.current;
    if (!el) return;

    const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (prefersReduced) {
      gsap.set(el, { opacity: 1, x: 0, y: 0, scale: 1 });
      return;
    }

    const from = FROM_MAP[animationType] || FROM_MAP.fadeIn;
    const toVars = { opacity: 1, x: 0, y: 0, scale: 1 };

    const ctx = gsap.context(() => {
      if (stagger) {
        const items = el.querySelectorAll(':scope > *');
        gsap.set(items, from);
        gsap.to(items, {
          ...toVars,
          duration: 0.75,
          delay,
          stagger: typeof stagger === 'number' ? stagger : 0.1,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 83%',
            toggleActions: 'play none none none',
          },
        });
      } else {
        gsap.fromTo(el, from, {
          ...toVars,
          duration: 0.9,
          delay,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: el,
            start: 'top 83%',
            toggleActions: 'play none none none',
          },
        });
      }
    }, elRef);

    return () => ctx.revert();
  }, [animationType, delay, stagger]);

  return (
    <div ref={elRef} className={className} style={{ opacity: 0 }} {...props}>
      {children}
    </div>
  );
}

export default ScrollAnimatedSection;
